"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TopicMolecule = void 0;
exports.groupDiastereotopicAtomIDs = groupDiastereotopicAtomIDs;
const getAllAtomsPaths_js_1 = require("../path/getAllAtomsPaths.js");
const getConnectivityMatrix_js_1 = require("../util/getConnectivityMatrix.js");
const tagAtom_js_1 = require("../util/tagAtom.js");
const getCanonizedDiaIDs_1 = require("./getCanonizedDiaIDs");
const getCanonizedHoseCodes_1 = require("./getCanonizedHoseCodes");
const getDiaIDsAndInfo_1 = require("./getDiaIDsAndInfo");
const getHeterotopicSymmetryRanks_1 = require("./getHeterotopicSymmetryRanks");
const getMoleculeWithH_1 = require("./getMoleculeWithH");
const getXMolecule_1 = require("./getXMolecule");
/**
 * This class deals with topicity information and hose codes
 * It is optimized to avoid recalculation of the same information
 */
class TopicMolecule {
    constructor(molecule, options = {}) {
        this.originalMolecule = molecule;
        this.options = { maxPathLength: 5, ...options };
        this.idCode = molecule.getIDCode();
        this.molecule = this.originalMolecule.getCompactCopy();
        this.molecule.ensureHelperArrays(molecule.getOCL().Molecule.cHelperNeighbours);
        this.molecule.ensureHelperArrays(this.molecule.getOCL().Molecule.cHelperNeighbours);
        this.cache = {};
    }
    ensureMapNo() {
        const existingMapNo = {};
        for (let i = 0; i < this.molecule.getAllAtoms(); i++) {
            const mapNo = this.molecule.getAtomMapNo(i);
            if (mapNo) {
                if (existingMapNo[mapNo]) {
                    throw new Error('The molecule contains several atoms with the same mapNo');
                }
                existingMapNo[mapNo] = true;
            }
        }
        let nextMapNo = 1;
        for (let i = 0; i < this.molecule.getAllAtoms(); i++) {
            const mapNo = this.molecule.getAtomMapNo(i);
            if (!mapNo) {
                while (existingMapNo[nextMapNo]) {
                    nextMapNo++;
                }
                existingMapNo[nextMapNo] = true;
                this.molecule.setAtomMapNo(i, nextMapNo, false);
            }
        }
    }
    getHoseFragment(rootAtoms, options = {}) {
        const { sphereSize = 2, tagAtoms = rootAtoms, tagAtomFct = tagAtom_js_1.tagAtom, } = options;
        this.moleculeWithH.ensureHelperArrays(this.moleculeWithH.getOCL().Molecule.cHelperNeighbours);
        const copy = this.moleculeWithH.getCompactCopy();
        copy.ensureHelperArrays(copy.getOCL().Molecule.cHelperNeighbours);
        for (let i = 0; i < copy.getAllAtoms(); i++) {
            copy.setAtomMass(i, copy.getAtomMass(i) + 2);
        }
        const atomMask = new Array(copy.getAllAtoms()).fill(false);
        const atomList = new Uint8Array(copy.getAllAtoms());
        const atomMapping = new Array(copy.getAllAtoms()).fill(-1);
        const Molecule = copy.getOCL().Molecule;
        const fragment = new Molecule(0, 0);
        fragment.setFragment(true);
        let min = 0;
        let max = 0;
        for (let sphere = 0; sphere <= sphereSize; sphere++) {
            if (max === 0) {
                for (const rootAtom of rootAtoms) {
                    atomList[max] = rootAtom;
                    atomMask[rootAtom] = 1;
                    max++;
                }
            }
            else {
                let newMax = max;
                for (let i = min; i < max; i++) {
                    const atom = atomList[i];
                    for (let j = 0; j < this.moleculeWithH.getAllConnAtoms(atom); j++) {
                        const connAtom = this.moleculeWithH.getConnAtom(atom, j);
                        if (!atomMask[connAtom]) {
                            atomMask[connAtom] = 1;
                            atomList[newMax++] = connAtom;
                        }
                    }
                }
                min = max;
                max = newMax;
            }
        }
        copy.copyMoleculeByAtoms(fragment, atomMask, true, atomMapping);
        for (let i = 0; i < fragment.getAllAtoms(); i++) {
            fragment.setAtomMass(i, fragment.getAtomMass(i) - 2);
        }
        for (const atom of tagAtoms) {
            tagAtomFct(fragment, atomMapping[atom]);
        }
        return fragment;
    }
    getAtomPaths(atom1, atom2, options = {}) {
        const { pathLength } = options;
        if (pathLength !== undefined && pathLength > this.options.maxPathLength) {
            throw new Error('The distance is too long, you should increase the maxPathLength when instantiating the TopicMolecule');
        }
        const atomPaths = this.atomsPaths[atom1];
        const minDistance = pathLength || 0;
        const maxDistance = pathLength || this.options.maxPathLength;
        const paths = [];
        for (let i = minDistance; i <= maxDistance; i++) {
            for (const atomPath of atomPaths[i]) {
                if (atomPath.path.at(-1) === atom2) {
                    paths.push(atomPath.path);
                }
            }
        }
        return paths;
    }
    get atomsPaths() {
        if (this.cache.atomsPaths)
            return this.cache.atomsPaths;
        this.cache.atomsPaths = (0, getAllAtomsPaths_js_1.getAllAtomsPaths)(this.moleculeWithH, {
            maxPathLength: this.options.maxPathLength,
        });
        return this.cache.atomsPaths;
    }
    toMolfile(options = {}) {
        const { version = 2 } = options;
        if (version === 2) {
            return this.molecule.toMolfile();
        }
        return this.molecule.toMolfileV3();
    }
    getMolecule() {
        return this.molecule;
    }
    /**
     * Returns a new TopicMolecule but will copy precalculated information
     * if possible (same idCode). This is very practical when expanding hydrogens
     * for example.
     * @param molecule
     * @returns
     */
    fromMolecule(molecule) {
        const idCode = molecule.getIDCode();
        if (idCode !== this.idCode) {
            // no way for optimisation
            return new TopicMolecule(molecule);
        }
        const topicMolecule = new TopicMolecule(molecule);
        topicMolecule.cache = {
            canonizedDiaIDs: this.cache.canonizedDiaIDs,
            canonizedHoseCodes: this.cache.canonizedHoseCodes,
        };
        return topicMolecule;
    }
    /**
     * Returns a molecule with all the hydrogens added. The order is NOT canonized
     */
    get moleculeWithH() {
        if (this.cache.moleculeWithH)
            return this.cache.moleculeWithH;
        this.cache.moleculeWithH = (0, getMoleculeWithH_1.getMoleculeWithH)(this.molecule, {
            maxNbAtoms: this.options.maxNbAtoms,
        });
        return this.cache.moleculeWithH;
    }
    get xMolecule() {
        if (this.cache.xMolecule)
            return this.cache.xMolecule;
        this.cache.xMolecule = (0, getXMolecule_1.getXMolecule)(this.moleculeWithH);
        return this.cache.xMolecule;
    }
    /**
     * This is related to the current moleculeWithH. The order is NOT canonized
     */
    get diaIDs() {
        if (this.cache.diaIDs)
            return this.cache.diaIDs;
        const diaIDs = [];
        for (let i = 0; i < this.moleculeWithH.getAllAtoms(); i++) {
            diaIDs.push(this.canonizedDiaIDs[this.finalRanks[i]]);
        }
        this.cache.diaIDs = diaIDs;
        return diaIDs;
    }
    /**
     * We return the atomIDs corresponding to the specified diaID as well has the attached hydrogens or heavy atoms
     * @param diaID
     * @returns
     */
    getDiaIDsObject() {
        return groupDiastereotopicAtomIDsAsObject(this.diaIDs, this.molecule, this.moleculeWithH);
    }
    /**
     * This is related to the current moleculeWithH. The order is NOT canonized
     */
    get hoseCodes() {
        if (this.cache.hoseCodes)
            return this.cache.hoseCodes;
        const hoseCodes = [];
        for (let i = 0; i < this.moleculeWithH.getAllAtoms(); i++) {
            hoseCodes.push(this.canonizedHoseCodes[this.finalRanks[i]]);
        }
        this.cache.hoseCodes = hoseCodes;
        return hoseCodes;
    }
    get canonizedDiaIDs() {
        if (this.cache.canonizedDiaIDs)
            return this.cache.canonizedDiaIDs;
        this.cache.canonizedDiaIDs = (0, getCanonizedDiaIDs_1.getCanonizedDiaIDs)(this);
        return this.cache.canonizedDiaIDs;
    }
    get canonizedHoseCodes() {
        if (this.cache.canonizedHoseCodes) {
            return this.cache.canonizedHoseCodes;
        }
        this.cache.canonizedHoseCodes = (0, getCanonizedHoseCodes_1.getCanonizedHoseCodes)(this);
        return this.cache.canonizedHoseCodes;
    }
    /**
     * Returns the distance matrix for the current moleculeWithH
     */
    get distanceMatrix() {
        return (0, getConnectivityMatrix_js_1.getConnectivityMatrix)(this.moleculeWithH, { pathLength: true });
    }
    get diaIDsAndInfo() {
        if (this.cache.diaIDsAndInfo)
            return this.cache.diaIDsAndInfo;
        this.cache.diaIDsAndInfo = (0, getDiaIDsAndInfo_1.getDiaIDsAndInfo)(this, this.canonizedDiaIDs);
        return this.cache.diaIDsAndInfo;
    }
    /**
     * Returns symmetryRanks for all the atoms including hydrogens. Those ranks
     * deals with topicity and is related to the current moleculeWithH.
     * In order to calculate the ranks we replace all the
     * hydrogens with a X atom.
     */
    get heterotopicSymmetryRanks() {
        if (this.cache.heterotopicSymmetryRanks) {
            return this.cache.heterotopicSymmetryRanks;
        }
        this.cache.heterotopicSymmetryRanks = (0, getHeterotopicSymmetryRanks_1.getHeterotopicSymmetryRanks)(this.xMolecule);
        return [...this.cache.heterotopicSymmetryRanks];
    }
    /**
     * Returns finalRanks for all the atoms including hydrogens. Those ranks
     * deals with topicity and is related to the current moleculeWithH.
     * All the atoms have a unique identifier.j
     * In order to calculate the ranks we replace all the
     * hydrogens with a X atom.
     */
    get finalRanks() {
        if (this.cache.finalRanks)
            return this.cache.finalRanks;
        this.cache.finalRanks = (0, getHeterotopicSymmetryRanks_1.getFinalRanks)(this.xMolecule);
        return this.cache.finalRanks;
    }
    toMolfileWithH(options = {}) {
        const { version = 2 } = options;
        if (version === 2) {
            return this.moleculeWithH.toMolfile();
        }
        return this.moleculeWithH.toMolfileV3();
    }
    /**
     * Returns an array of objects containing the oclID and the corresponding hydrogens and atoms
     * for the specified atomLabel (if any)
     * This always applied to the molecule with expanded hydrogens and chirality
     * @param options
     * @returns
     */
    getGroupedDiastereotopicAtomIDs(options = {}) {
        return groupDiastereotopicAtomIDs(this.diaIDs, this.moleculeWithH, options);
    }
    /**
     * This method returns a mapping between the diaIDs of the current molecule.
     * It expects that the initial molfile and the final molfile contains atomMapNo
     * in order to track which atom becomes which one.
     */
    getDiaIDsMapping(molecule) {
        const topicMolecule = new TopicMolecule(molecule);
        const originalDiaIDs = this.diaIDsAndInfo.filter((diaID) => diaID.atomMapNo);
        const destinationDiaIDs = topicMolecule.diaIDsAndInfo.filter((diaID) => diaID.atomMapNo);
        const mapping = {};
        // we first check all the atoms present in the molfile
        for (const destinationDiaID of destinationDiaIDs) {
            const originalDiaID = originalDiaIDs.find((diaID) => diaID.atomMapNo === destinationDiaID.atomMapNo);
            const newIDCode = destinationDiaID.idCode;
            const oldIDCode = originalDiaID.idCode;
            if (oldIDCode in mapping) {
                if (mapping[oldIDCode] !== newIDCode) {
                    mapping[oldIDCode] = undefined;
                }
            }
            else {
                mapping[oldIDCode] = newIDCode;
            }
        }
        // we now check all the attached hydrogens that are not defined in the molfile and were not yet mapped
        for (const destinationDiaID of destinationDiaIDs) {
            const originalDiaID = originalDiaIDs.find((diaID) => diaID.atomMapNo === destinationDiaID.atomMapNo);
            for (let i = 0; i < originalDiaID.attachedHydrogensIDCodes.length; i++) {
                const oldHydrogenIDCode = originalDiaID.attachedHydrogensIDCodes[i];
                if (mapping[oldHydrogenIDCode])
                    continue;
                const newHydrogenIDCode = destinationDiaID.attachedHydrogensIDCodes[i];
                if (oldHydrogenIDCode && newHydrogenIDCode) {
                    if (oldHydrogenIDCode in mapping) {
                        if (mapping[oldHydrogenIDCode] !== newHydrogenIDCode) {
                            mapping[oldHydrogenIDCode] = undefined;
                        }
                    }
                    else {
                        mapping[oldHydrogenIDCode] = newHydrogenIDCode;
                    }
                }
            }
        }
        return mapping;
    }
}
exports.TopicMolecule = TopicMolecule;
function groupDiastereotopicAtomIDs(diaIDs, molecule, options = {}) {
    const diaIDsObject = groupDiastereotopicAtomIDsAsObject(diaIDs, molecule, molecule, options);
    return Object.keys(diaIDsObject).map((key) => diaIDsObject[key]);
}
function groupDiastereotopicAtomIDsAsObject(diaIDs, molecule, moleculeWithH, options = {}) {
    const { atomLabel } = options;
    const diaIDsObject = {};
    for (let i = 0; i < diaIDs.length; i++) {
        if (!atomLabel || moleculeWithH.getAtomLabel(i) === atomLabel) {
            const diaID = diaIDs[i];
            if (!diaIDsObject[diaID]) {
                diaIDsObject[diaID] = {
                    counter: 0,
                    atoms: [],
                    oclID: diaID,
                    atomLabel: moleculeWithH.getAtomLabel(i),
                    heavyAtoms: [],
                    attachedHydrogens: [],
                    existingAtoms: [],
                };
            }
            if (moleculeWithH.getAtomicNo(i) === 1) {
                const connected = moleculeWithH.getConnAtom(i, 0);
                if (!diaIDsObject[diaID].heavyAtoms.includes(connected)) {
                    diaIDsObject[diaID].heavyAtoms.push(connected);
                }
                if (molecule.getAtomicNo(i)) {
                    diaIDsObject[diaID].existingAtoms.push(i);
                }
                else if (!diaIDsObject[diaID].existingAtoms.includes(connected)) {
                    diaIDsObject[diaID].existingAtoms.push(connected);
                }
            }
            else {
                for (let j = 0; j < moleculeWithH.getAllConnAtoms(i); j++) {
                    const connected = moleculeWithH.getConnAtom(i, j);
                    if (moleculeWithH.getAtomicNo(connected) === 1) {
                        diaIDsObject[diaID].attachedHydrogens.push(connected);
                    }
                }
                diaIDsObject[diaID].existingAtoms.push(i);
            }
            diaIDsObject[diaID].counter++;
            diaIDsObject[diaID].atoms.push(i);
        }
    }
    for (const diaID in diaIDsObject) {
        diaIDsObject[diaID].existingAtoms.sort((a, b) => a - b);
        diaIDsObject[diaID].attachedHydrogens.sort((a, b) => a - b);
    }
    return diaIDsObject;
}
//# sourceMappingURL=TopicMolecule.js.map